import type { Ref, UnwrapNestedRefs } from 'vue';
import { isRef, nextTick, reactive, ref, watch } from 'vue';
import type { SchemaValidationResult, ValidationSchema } from './rules';
import { validate as validateSchema } from './rules';

export interface FormModelInterface<T> {
  model: UnwrapNestedRefs<T>;
  errors: UnwrapNestedRefs<SchemaValidationResult<T>>;
  hasChanged: Ref<boolean>;
  validate: () => { result: SchemaValidationResult<T> | undefined; success: boolean };
  reset: () => void;
  resetModel: () => void;
}
export const useFormModel = <T extends object>(
  o: T | Ref<T | undefined>,
  rules?: ValidationSchema<T>,
): [FormModelInterface<T>, UnwrapNestedRefs<T>, UnwrapNestedRefs<SchemaValidationResult<T>>, Ref<boolean>] => {
  const initialState = { ...(isRef(o) ? o.value || ({} as T) : o) };
  const model = reactive<T>(isRef(o) ? o.value || ({} as T) : o);
  const errors = reactive({} as SchemaValidationResult<T>);
  const hasChanged = ref(false);

  watch(model, () => (hasChanged.value = true));

  /**
   * Clear error model and has changed state
   */
  const reset = () => {
    nextTick(() => {
      for (const key in errors) delete errors[key];
      hasChanged.value = false;
    });
  };

  /**
   * Clear model, clear error model and has changed state
   */
  const resetModel = () => {
    nextTick(() => {
      for (const key in model) delete model[key];
      hasChanged.value = false;
      Object.assign(model, initialState);
    });
    reset();
  };

  const validate = () => {
    if (!rules) return { result: undefined, success: true };
    const validationState = model as T;
    const validationResult = validateSchema(validationState, rules);
    for (const key in errors) delete errors[key];
    Object.assign(errors, validationResult);
    return { result: validationResult, success: Object.keys(validationResult).length === 0 };
  };

  return [
    {
      model,
      reset,
      validate,
      resetModel,
      errors,
      hasChanged,
    },
    model,
    errors,
    hasChanged,
  ];
};
